import { useMemo } from 'react';

type AccountType = {
  _id: string;
  accountType?: string;
  accountTag?: string;
  isAccountHolder?: boolean;
  isPrepaid?: boolean;
  isTaxAllowed?: boolean;
};

type Props = {
  existingAccountsValues: AccountType[];
  selectedAccountId: string;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
};

export const useExistingAccount = ({ existingAccountsValues, selectedAccountId, setFieldValue }: Props) => {
  const selectedAccount = useMemo(() => {
    return existingAccountsValues.find(acc => acc._id === selectedAccountId);
  }, [selectedAccountId, existingAccountsValues]);

  const applyAccountValues = (accountId: string) => {
    const acc = existingAccountsValues.find(a => a._id === accountId);
    if (!acc) return;

    setFieldValue('accountType', acc.accountType || 'individual', false);
    setFieldValue('accountTag', acc.accountTag || '', false);
    setFieldValue('isAccountHolder', acc.isAccountHolder ?? false, false);
    setFieldValue('isPrepaid', acc.isPrepaid ?? true, false);
    setFieldValue('isTaxAllowed', acc.isTaxAllowed ?? false, false);
  };

  const resetAccountValues = () => {
    setFieldValue('accountType', 'individual', false);
    setFieldValue('accountTag', '', false);
    setFieldValue('isAccountHolder', false, false);
    setFieldValue('isPrepaid', true, false);
    setFieldValue('isTaxAllowed', false, false);
  };

  const isExistingAccountSelected = !!selectedAccountId;

  const isFieldDisabled = useMemo(() => {
    if (!selectedAccount) return false;

    // 👉 individual case → never disable
    if (selectedAccount.accountType === 'individual') return false;

    // 👉 company case → disable
    return true;
  }, [selectedAccount]);

  return {
    selectedAccount,
    applyAccountValues,
    resetAccountValues,
    isExistingAccountSelected,
    isFieldDisabled,
  };
};
